import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import {
  getInsuranceFlowHistoryLabel,
  InsuranceFlowHistoryStatus,
} from 'src/app/_enums/insurance-flow/insurance-flow-history-status.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { formatDateTimeToFrCh } from 'src/app/_helpers/utils';

import { TaskLogDiffModalData } from '../task-log-diff.types';
import { TaskLogLeadDiffComponent } from '../task-log-lead-diff/task-log-lead-diff.component';
import { TaskLogProposalDiffComponent } from '../task-log-proposal-diff/task-log-proposal-diff.component';

@Component({
  selector: 'app-task-log-diff-modal',
  standalone: true,
  imports: [
    CommonModule,
    NgbModalModule,
    TaskLogLeadDiffComponent,
    TaskLogProposalDiffComponent,
  ],
  templateUrl: './task-log-diff-modal.component.html',
  styleUrl: './task-log-diff-modal.component.css',
})
export class TaskLogDiffModalComponent {
  @Input() diffData: TaskLogDiffModalData | null = null;

  @Output() closed = new EventEmitter<void>();

  @ViewChild('diffModal')
  private readonly diffModalTemplate?: TemplateRef<HTMLElement>;

  private modalRef?: NgbModalRef;

  constructor(private readonly modalService: NgbModal) {}

  protected get currentData(): TaskLogDiffModalData | null {
    return this.diffData;
  }

  protected get statusLabel(): string {
    if (!this.diffData?.currentElement.status) {
      return '-';
    }

    return getInsuranceFlowHistoryLabel(this.diffData.currentElement.status);
  }

  protected get displayDate(): string {
    const rawDate =
      this.diffData?.currentElement.updatedOn ??
      this.diffData?.currentElement.endedOn;
    return formatDateTimeToFrCh(rawDate) ?? '-';
  }

  protected get displayName(): string {
    const currentElement = this.diffData?.currentElement;
    return (
      currentElement?.updatedByFullName?.trim() ||
      currentElement?.endedByFullName?.trim() ||
      '-'
    );
  }

  protected get compareProposalSnapshot(): boolean {
    const status = this.diffData?.currentElement.status;
    return (
      status !== InsuranceFlowHistoryStatus.OFFER_CREATED &&
      status !== InsuranceFlowHistoryStatus.OFFER_SENT &&
      status !== InsuranceFlowHistoryStatus.OFFER_REJECTED &&
      status !== InsuranceFlowStatus.OFFER_SIGNED
    );
  }

  protected get displayedProposalSnapshot() {
    return this.compareProposalSnapshot
      ? (this.diffData?.proposalBaseline ?? null)
      : (this.diffData?.currentElement.proposalSnapshot ?? null);
  }

  protected get proposalSnapshotBaseline() {
    return this.compareProposalSnapshot
      ? (this.diffData?.currentElement.proposalSnapshot ?? null)
      : null;
  }

  openModal(diffData?: TaskLogDiffModalData | null): void {
    if (diffData) {
      this.diffData = diffData;
    }

    if (!this.diffModalTemplate || this.modalRef || !this.diffData) {
      return;
    }

    this.modalRef = this.modalService.open(this.diffModalTemplate, {
      centered: true,
      size: 'xl',
      windowClass: 'task-log-diff-modal-window',
    });

    this.modalRef.closed.subscribe(() => {
      this.modalRef = undefined;
      this.closed.emit();
    });

    this.modalRef.dismissed.subscribe(() => {
      this.modalRef = undefined;
      this.closed.emit();
    });
  }

  closeModal(): void {
    if (!this.modalRef) {
      return;
    }

    this.modalRef.close();
  }
}
